import React from "react";
import { formSteps } from "../../../config/formSteps";
import {
  shouldSkipStep,
  getStepNumber,
  getNextStepId,
} from "../../../utils/stepLogic";
import type { PropertyFormData, StepId } from "../../../types";
import { useCompletedSteps, useLastCompletedStepId } from "@/stores";
import { CheckIcon } from "@heroicons/react/24/solid";
import {
  stepContainer,
  stepNumber,
  stepLabel,
  stepValue,
} from "./index.styles";
import { getStepLabel, getStepValue, areAllStepsFilled } from "./utils";

interface PropertyDetailsProps {
  formData: Partial<PropertyFormData>;
  currentStepId?: StepId;
  onStepClick?: (stepId: StepId) => void;
}

const PropertyDetails: React.FC<PropertyDetailsProps> = ({
  formData,
  currentStepId,
  onStepClick,
}) => {
  const completedSteps = useCompletedSteps();
  const lastCompletedStepId = useLastCompletedStepId();

  // Get all visible steps (not skipped)
  const visibleSteps = formSteps.filter(
    (step) => !shouldSkipStep(step.id, formData)
  );

  // Determine the next step after the last completed one
  // If no steps are completed, the first visible step is the "next" one.
  const nextStepId = lastCompletedStepId
    ? getNextStepId(lastCompletedStepId as StepId, formData)
    : visibleSteps[0]?.id;

  // Filter to show:
  // 1. Completed steps
  // 2. Current step
  // 3. The next step after the last completed one (to show progress/what's coming)
  const displayedSteps = visibleSteps.filter((step) => {
    const isCompleted = completedSteps.includes(step.id);
    const isCurrent = step.id === currentStepId;
    const isNext = step.id === nextStepId;

    return isCompleted || isCurrent || isNext;
  });

  // Check if we're on contact step and all other steps are filled
  const isOnContactStep = currentStepId === "contact";
  const allFilled = areAllStepsFilled(formData);
  const showSuccessState = isOnContactStep && allFilled;

  if (displayedSteps.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p className="text-sm">
          Füllen Sie das Formular aus, um Details zu sehen
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {displayedSteps.map((step) => {
        const stepNum = getStepNumber(step.id, formData);
        const isActive = step.id === currentStepId;
        const isCompleted = completedSteps.includes(step.id);
        const label = getStepLabel(step.id, formData);
        const value = getStepValue(step.id, formData);
        const hasValue = value.trim() !== "";

        // For contact step, show success state if all steps are filled
        const isContactStep = step.id === "contact";
        const showCheckmark = isContactStep && showSuccessState;

        // Determine state for styling
        let state: "active" | "completed" | "pending" | "success" = "pending";
        if (showCheckmark) state = "success";
        else if (isActive) state = "active";
        else if (isCompleted) state = "completed";

        const content = (
          <div
            className={stepContainer({
              state,
              isHoverable: !!onStepClick,
            })}
          >
            {/* Step Number / Checkmark */}
            <div className={stepNumber({ state })}>
              {showCheckmark || (isCompleted && !isActive) ? (
                <CheckIcon className="h-4 w-4" />
              ) : (
                stepNum
              )}
            </div>

            {/* Label and Value */}
            <div className="flex-1 flex justify-between items-center min-w-0">
              <span className={stepLabel({ state })}>{label}</span>
              <span className={stepValue({ state, hasValue })}>
                {hasValue ? value : "Nicht ausgefüllt"}
              </span>
            </div>
          </div>
        );

        if (onStepClick) {
          return (
            <button
              key={step.id}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onStepClick(step.id);
              }}
              className="w-full"
            >
              {content}
            </button>
          );
        }

        return <div key={step.id}>{content}</div>;
      })}
    </div>
  );
};

export default PropertyDetails;
